/**
 * src/components/AuthGuard.tsx
 * 認証ガード（admin / assistant 両対応）
 */

import { useEffect, useState, createContext, useContext } from 'react'

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://mangaos.studio-nekonomiya.workers.dev/api'

export type AuthUser = {
  id:             string
  email:          string
  role:           'admin' | 'assistant'
  assistant_id:   string | null
  assistant_name: string | null
  login_count:    number
  must_change_password?: boolean   // 初期（仮）パスワードの変更が必要
  // 追加認証の状態（案内表示用。権限判定はサーバーが行う）
  mfa_method?:         'none' | 'passkey' | 'email'
  mfa_setup_required?: boolean
  mfa_required?:       boolean
  passkey_count?:      number
  mfa_options?:        { passkey: boolean; email: boolean }   // サーバーで有効な方式
}

type AuthContextValue = {
  user:        AuthUser | null
  logout:      () => Promise<void>
  refreshUser: () => Promise<void>
  loading:     boolean
  isAdmin:     boolean
}

const AuthContext = createContext<AuthContextValue>({
  user: null, logout: async () => {}, refreshUser: async () => {}, loading: true, isAdmin: false,
})

export function useAuth(): AuthContextValue {
  return useContext(AuthContext)
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user,    setUser]    = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchMe = async () => {
    const res = await fetch(`${API_BASE}/auth/me`, { credentials: 'include' })
    if (res.ok) {
      const data = await res.json()
      if (data.authenticated) {
        setUser({ ...data.user, login_count: data.user.login_count ?? 0 })
        // ★ 追加認証の初期設定が必要な間は、アカウント設定以外に進めない
        //   （サーバー側でも保護対象APIは 403 MFA_SETUP_REQUIRED で拒否される）
        if (data.user.mfa_setup_required && !window.location.pathname.startsWith('/account')) {
          window.location.replace(data.user.must_change_password
            ? '/account?force_change_password=1'
            : '/account?mfa_setup=1')
        }
        return
      }
    }
    if (!window.location.pathname.startsWith('/login')) {
      window.location.replace('/login')
    }
  }

  useEffect(() => {
    fetchMe().catch(() => {
      if (!window.location.pathname.startsWith('/login')) {
        window.location.replace('/login')
      }
    }).finally(() => setLoading(false))
  }, [])

  const logout = async () => {
    await fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' })
    window.location.replace('/login')
  }

  const refreshUser = async () => {
    await fetchMe().catch(() => {})
  }

  return (
    <AuthContext.Provider value={{
      user,
      logout,
      refreshUser,
      loading,
      isAdmin: user?.role === 'admin',
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function AuthGuard({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--ink-3)', fontSize: 13,
      }}>
        <div style={{ fontFamily: 'var(--font-mono)' }}>LOADING...</div>
      </div>
    )
  }

  if (!user) return null
  return <>{children}</>
}